import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Mail, Phone, MapPin, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { trackEvent } from "@/lib/analytics";

const socialIcon = { Facebook, Instagram, Twitter, YouTube: Youtube };

export const Footer = () => {
  const { data: content } = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      trackEvent("form_submit", { form: "newsletter", location: "footer" });
      toast.success("You're subscribed! Thank you for joining us.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contact = content?.contact || {};
  const socials = contact.socials || [];

  return (
    <footer className="hero-gradient text-white mt-20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="font-serif font-bold text-xl">Satish Chapagain</div>
          <div className="text-sm text-blue-200 mt-1">for Maryland State Delegate &middot; District 7A</div>
          <p className="mt-4 font-serif italic text-blue-100 text-lg">"People First. Community Always."</p>
          <Button asChild className="mt-5 bg-donate hover:bg-donate-hover text-white rounded-full font-semibold" data-testid="footer-donate-btn">
            <Link to="/donate"><Heart className="w-4 h-4 mr-1.5" /> Donate</Link>
          </Button>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Quick Links</h4>
          <ul className="space-y-2 text-sm text-blue-100">
            {[["Home", "/"], ["About", "/about"], ["Priorities", "/priorities"], ["Get Involved", "/get-involved"], ["Vote", "/vote"], ["Contact", "/contact"]].map(([l, to]) => (
              <li key={to}><Link to={to} className="hover:text-white transition-colors" data-testid={`footer-link-${l.toLowerCase().replace(/ /g, "-")}`}>{l}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Contact</h4>
          <ul className="space-y-3 text-sm text-blue-100">
            {contact.email && <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /><a href={`mailto:${contact.email}`} className="hover:text-white break-all">{contact.email}</a></li>}
            {contact.email_alt && <li className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 shrink-0" /><a href={`mailto:${contact.email_alt}`} className="hover:text-white break-all">{contact.email_alt}</a></li>}
            {contact.phone && <li className="flex items-start gap-2"><Phone className="w-4 h-4 mt-0.5 shrink-0" /><a href={`tel:${contact.phone}`} className="hover:text-white">{contact.phone}</a></li>}
            {contact.address && <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /><span>{contact.address}</span></li>}
          </ul>
          <div className="flex gap-3 mt-4">
            {socials.map((s) => {
              const Icon = socialIcon[s.platform] || Mail;
              return (
                <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform}
                   className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                   data-testid={`footer-social-${s.platform.toLowerCase()}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Stay Updated</h4>
          <form onSubmit={subscribe} className="space-y-2" data-testid="footer-newsletter-form">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address"
              className="bg-white/10 border-white/20 text-white placeholder:text-blue-200"
              data-testid="footer-newsletter-email"
            />
            <Button type="submit" disabled={loading} className="w-full bg-brightblue hover:bg-brightblue-hover text-white font-semibold" data-testid="footer-newsletter-submit">
              {loading ? "Joining..." : "Join the Movement"}
            </Button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-blue-200">
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white" data-testid="footer-terms">Terms</Link>
            <Link to="/admin" className="hover:text-white" data-testid="footer-admin">Admin</Link>
          </div>
          <div className="text-center sm:text-right">
            {content?.authority_line || "By Authority: Friends of Satish Chapagain, Gyan Hari Ghimire, Treasurer"}
          </div>
        </div>
      </div>
    </footer>
  );
};
